import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { PageHeader } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { exportToExcel, ExcelColumn } from '../utils/exportToExcel';

interface PostFermentationProcess {
  id: number;
  batch_id: number;
  squeezingDate?: string;
  daborbageDate?: string;
  sg?: number;
  ph?: number;
  malic_acid?: number;
  va?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  batchNumber?: string;
  batch?: {
    id: number;
    batch_id: string;
    batch_name: string;
    vineyard?: {
      name: string;
      location?: string;
    };
  };
  wineBatch?: {
    id: number;
    batch_id: string;
    batch_name: string;
    vineyard?: {
      name: string;
      location?: string;
    };
  };
}

interface PostFermentationFormValues {
  batch_id: number;
  squeezingDate?: string;
  daborbageDate?: string;
  sg?: number | string;
  ph?: number | string;
  malic_acid?: number | string;
  va?: number | string;
  notes?: string;
}

// Mapping function to normalize data from the API
const mapApiDataToPostFermentation = (apiData: any): PostFermentationProcess => {
  console.log('Raw API data being mapped for PostFermentation:', apiData);

  // Basic mapping for top-level fields
  const mapped: PostFermentationProcess = {
    id: apiData.id,
    batch_id: apiData.batch_id,
    squeezingDate: apiData.squeezingDate || apiData.squeezing_date,
    daborbageDate: apiData.daborbageDate || apiData.daborbage_date,
    sg: apiData.sg,
    ph: apiData.ph,
    malic_acid: apiData.malic_acid,
    va: apiData.va,
    notes: apiData.notes,
    createdAt: apiData.createdAt || apiData.created_at || new Date().toISOString(),
    updatedAt: apiData.updatedAt || apiData.updated_at || new Date().toISOString(),
    batchNumber: apiData.batchNumber || apiData.batch_number
  };

  // Map wineBatch - prefer direct wineBatch if available
  if (apiData.wineBatch) {
    console.log('Using direct wineBatch data from API response');
    mapped.wineBatch = {
      id: apiData.wineBatch.id,
      batch_id: apiData.wineBatch.batch_id || apiData.wineBatch.batchId || `#${apiData.wineBatch.id}`,
      batch_name: apiData.wineBatch.batch_name || apiData.wineBatch.batchName || 'Unnamed Batch'
    };

    // Map vineyard within wineBatch
    if (apiData.wineBatch.vineyard) {
      console.log('Found vineyard in wineBatch:', apiData.wineBatch.vineyard);
      mapped.wineBatch.vineyard = {
        name: apiData.wineBatch.vineyard.name,
        location: apiData.wineBatch.vineyard.location
      };
    } else {
      console.log('No vineyard data in wineBatch');
    }
  }

  // Map batch - prefer batch if available
  if (apiData.batch) {
    console.log('Using batch data from API response');
    mapped.batch = {
      id: apiData.batch.id,
      batch_id: apiData.batch.batch_id || apiData.batch.batchId || `#${apiData.batch.id}`,
      batch_name: apiData.batch.batch_name || apiData.batch.batchName || 'Unnamed Batch'
    };

    // Map vineyard within batch
    if (apiData.batch.vineyard) {
      console.log('Found vineyard in batch:', apiData.batch.vineyard);
      mapped.batch.vineyard = {
        name: apiData.batch.vineyard.name,
        location: apiData.batch.vineyard.location
      };
    } else {
      console.log('No vineyard data in batch');
    }
  }

  // Fallback to fermentation.wineBatch if direct wineBatch/batch is not available
  else if (apiData.fermentation && apiData.fermentation.wineBatch) {
    console.log('Falling back to fermentation.wineBatch data');
    mapped.wineBatch = {
      id: apiData.fermentation.wineBatch.id,
      batch_id: apiData.fermentation.wineBatch.batch_id || apiData.fermentation.wineBatch.batchId || `#${apiData.fermentation.wineBatch.id}`,
      batch_name: apiData.fermentation.wineBatch.batch_name || apiData.fermentation.wineBatch.batchName || 'Unnamed Batch'
    };

    // Map vineyard within wineBatch
    if (apiData.fermentation.wineBatch.vineyard) {
      console.log('Found vineyard in fermentation.wineBatch:', apiData.fermentation.wineBatch.vineyard);
      mapped.wineBatch.vineyard = {
        name: apiData.fermentation.wineBatch.vineyard.name,
        location: apiData.fermentation.wineBatch.vineyard.location
      };
    } else {
      console.log('No vineyard data in fermentation.wineBatch');
    }

    // Also populate batch for compatibility
    mapped.batch = { ...mapped.wineBatch };
  } else {
    console.log('No batch or wineBatch data available in API response');
  }

  console.log('Mapped PostFermentation record:', mapped);
  return mapped;
};

const PostFermentationSchema = Yup.object().shape({
  batch_id: Yup.number().required('Wine batch is required'),
  squeezingDate: Yup.date()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable(),
  daborbageDate: Yup.date()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable(),
  sg: Yup.number().transform((value, originalValue) => (originalValue === '' ? undefined : value)).nullable(),
  ph: Yup.number().transform((value, originalValue) => (originalValue === '' ? undefined : value)).nullable(),
  malic_acid: Yup.number().transform((value, originalValue) => (originalValue === '' ? undefined : value)).nullable(),
  va: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .min(0, 'Volatile Acidity cannot be less than 0'),
  notes: Yup.string()
});

const PostFermentation: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [postFermentations, setPostFermentations] = useState<PostFermentationProcess[]>([]);
  const [wineBatches, setWineBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingPostFermentation, setEditingPostFermentation] = useState<PostFermentationProcess | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [postFermentationsRes, fermentationsRes, batchesRes] = await Promise.all([
          api.get('/api/post-fermentation'),
          api.get('/api/fermentation'),
          api.get('/api/batch')
        ]);

        console.log('Raw Post-Fermentation data from API:', postFermentationsRes.data);
        console.log('Fetched fermentations:', fermentationsRes.data);
        console.log('Fetched batches:', batchesRes.data);

        // Process the data through our mapping function
        const recordsData = Array.isArray(postFermentationsRes.data)
          ? postFermentationsRes.data
          : [postFermentationsRes.data];

        const mappedRecords = recordsData.map(mapApiDataToPostFermentation);
        console.log('Mapped Post-Fermentation records:', mappedRecords);

        // Compute allowed batch IDs from fermentations (fallback-safe, no need for nested wineBatch)
        const allowedBatchIds = new Set<number>(
          (Array.isArray(fermentationsRes.data) ? fermentationsRes.data : [])
            .map((f: any) => Number(f.batch_id ?? f.batchId))
            .filter((id: any) => Number.isFinite(id))
        );

        // Restrict selectable batches to those that have at least one fermentation
        const batchesWithFermentation = (Array.isArray(batchesRes.data) ? batchesRes.data : [])
          .filter((batch: any) => allowedBatchIds.has(Number(batch.id)));

        console.log('Wine batches with fermentation (derived by IDs):', batchesWithFermentation);

        setPostFermentations(mappedRecords);
        setWineBatches(batchesWithFermentation);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [tabVisible]);

  const openModal = (postFermentation: PostFermentationProcess | null = null) => {
    setEditingPostFermentation(postFermentation);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPostFermentation(null);
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this post-fermentation record?')) return;
    try {
      await api.delete(`/api/post-fermentation/${id}`);
      setPostFermentations(prev => prev.filter(pf => pf.id !== id));
      setSuccessMessage('Post-fermentation record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err);
    }
  };


  // Get post-fermentation data with proper batch information
  const getUpdatedPostFermentation = async (postFermentationId: number) => {
    try {
      const response = await api.get(`/api/post-fermentation/${postFermentationId}`);
      console.log('Raw updated post-fermentation data:', response.data);

      // Apply the mapping function to normalize the data
      const mappedRecord = mapApiDataToPostFermentation(response.data);
      console.log('Mapped updated post-fermentation:', mappedRecord);

      return mappedRecord;
    } catch (error) {
      console.error('Error fetching updated post-fermentation process:', error);
      throw error;
    }
  };

  const handleSubmit = async (values: PostFermentationFormValues, { resetForm, setSubmitting }: any) => {
    try {
      // Transform camelCase to snake_case for API compatibility
      const apiValues = {
        batch_id: values.batch_id,
        squeezing_date: values.squeezingDate,
        daborbage_date: values.daborbageDate,
        sg: values.sg === '' || values.sg === undefined ? null : values.sg,
        ph: values.ph === '' || values.ph === undefined ? null : values.ph,
        malic_acid: values.malic_acid === '' || values.malic_acid === undefined ? null : values.malic_acid,
        va: values.va === '' || values.va === undefined ? null : values.va,
        notes: values.notes
      };

      console.log('Form values before API submission:', values);
      console.log('Transformed API values:', apiValues);

      let updatedPostFermentation: PostFermentationProcess;

      if (editingPostFermentation) {
        // Update existing post-fermentation process
        await api.put(`/api/post-fermentation/${editingPostFermentation.id}`, apiValues);
        // Get the updated data with all associations
        updatedPostFermentation = await getUpdatedPostFermentation(editingPostFermentation.id);
        setPostFermentations(postFermentations.map(pf => (pf.id === editingPostFermentation.id ? updatedPostFermentation : pf)));
        setSuccessMessage('Post-fermentation process updated successfully!');
      } else {
        // Create new post-fermentation process
        const response = await api.post('/api/post-fermentation', apiValues);
        // Get the new data with all associations
        updatedPostFermentation = await getUpdatedPostFermentation(response.data.id);
        setPostFermentations([updatedPostFermentation, ...postFermentations]);
        setSuccessMessage('Post-fermentation process started successfully!');
      }

      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving post-fermentation process:', err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Export post‑fermentation records to Excel.
  const handleExportToExcel = () => {
    if (!postFermentations.length) return;

    const columns: ExcelColumn<PostFermentationProcess>[] = [
      {
        header: 'Batch',
        accessor: (p) => {
          const wb = p.wineBatch || p.batch;
          if (!wb) return 'Unknown Batch';
          const vineyardName = wb.vineyard?.name;
          const batchId = wb.batch_id;
          const batchName = wb.batch_name;
          return vineyardName
            ? `${vineyardName} - ${batchId}`
            : batchId || batchName || 'Unknown Batch';
        },
      },
      {
        header: 'Pressing Date',
        accessor: (p) => p.squeezingDate || '',
      },
      {
        header: 'Débourbage Date',
        accessor: (p) => p.daborbageDate || '',
      },
      {
        header: 'VA',
        accessor: (p) => (p.va != null ? p.va : ''),
      },
      {
        header: 'SG',
        accessor: (p) => (p.sg != null ? p.sg : ''),
      },
      {
        header: 'pH',
        accessor: (p) => (p.ph != null ? p.ph : ''),
      },
      {
        header: 'Malic Acid',
        accessor: (p) => (p.malic_acid != null ? p.malic_acid : ''),
      },
    ];

    exportToExcel({
      fileName: `post-fermentation-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'PostFermentation',
      rows: postFermentations,
      columns,
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Post-Fermentation Management"
        subtitle="Track and manage post-fermentation processes."
        variant="compact"
        actions={(
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleExportToExcel}
            className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
          >
            Export to Excel
          </button>
          <PermissionButton
            resource="postFermentation"
            action="create"
            onClick={() => openModal()}
          >
            Add Post-Fermentation Data
          </PermissionButton>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-monday-border">
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6">Batch</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Pressing Date</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Débourbage Date</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">VA</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">SG</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">pH</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text">Malic Acid</th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-monday-border-light bg-white">
                    {postFermentations.length > 0 ? (
                      postFermentations.map((process) => (
                        <tr key={process.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex flex-col">
                              <span className="font-medium text-monday-text">
                                {(process.wineBatch?.vineyard?.name || process.batch?.vineyard?.name) ? `${process.wineBatch?.vineyard?.name || process.batch?.vineyard?.name} - ${process.wineBatch?.batch_id || process.batch?.batch_id || 'No ID'}` : process.wineBatch?.batch_id || process.batch?.batch_id || 'Unknown Batch'}
                              </span>
                              <span className="text-monday-text-muted">{process.wineBatch?.batch_name || process.batch?.batch_name || 'No batch name'}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {process.squeezingDate ? new Date(process.squeezingDate).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {process.daborbageDate ? new Date(process.daborbageDate).toLocaleDateString() : 'Not set'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {process.va !== undefined && process.va !== null ? `${process.va}` : 'Not set'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{process.sg ?? 'Not set'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{process.ph ?? 'Not set'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{process.malic_acid ?? 'Not set'}</td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-2">
                            <PermissionButton
                              resource="postFermentation"
                              action="update"
                              onClick={() => openModal(process)}
                              variant="secondary"
                              className="!px-2 !py-1 text-sm"
                            >
                              Edit
                            </PermissionButton>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(process.id)}
                                className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 border border-red-200"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="py-4 text-center text-sm text-monday-text-muted">
                          No post-fermentation processes found. Add one to get started!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="sm:hidden space-y-4">
          {postFermentations.length > 0 ? (
            postFermentations.map((process) => (
              <div key={process.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-base text-monday-text">
                      {(process.wineBatch?.vineyard?.name || process.batch?.vineyard?.name) ? `${process.wineBatch?.vineyard?.name || process.batch?.vineyard?.name} - ${process.wineBatch?.batch_id || process.batch?.batch_id || 'No ID'}` : process.wineBatch?.batch_id || process.batch?.batch_id || 'Unknown Batch'}
                    </div>
                    <div className="text-xs text-monday-text-muted">{process.wineBatch?.batch_name || process.batch?.batch_name || 'No batch name'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <PermissionButton
                      resource="postFermentation"
                      action="update"
                      onClick={() => openModal(process)}
                      variant="secondary"
                      className="!py-1 !px-3 !text-xs"
                    >
                      Edit
                    </PermissionButton>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(process.id)}
                        className="py-1 px-3 rounded bg-red-50 text-red-700 font-medium text-xs active:bg-red-100 border border-red-200"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-monday-text">
                  <div><span className="font-medium">Pressing:</span> {process.squeezingDate ? new Date(process.squeezingDate).toLocaleDateString() : 'Not set'}</div>
                  <div><span className="font-medium">Débourbage:</span> {process.daborbageDate ? new Date(process.daborbageDate).toLocaleDateString() : 'Not set'}</div>
                  <div><span className="font-medium">VA:</span> {process.va !== undefined && process.va !== null ? `${process.va}` : 'Not set'}</div>
                  <div><span className="font-medium">SG:</span> {process.sg ?? 'Not set'}</div>
                  <div><span className="font-medium">pH:</span> {process.ph ?? 'Not set'}</div>
                  <div><span className="font-medium">Malic Acid:</span> {process.malic_acid ?? 'Not set'}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-monday-text-muted py-8">No post-fermentation processes found. Add one to get started!</div>
          )}
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingPostFermentation ? 'Edit Post-Fermentation Process' : 'Add Post-Fermentation Process'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              batch_id: editingPostFermentation?.batch_id || (wineBatches.length > 0 ? wineBatches[0].id : 0),
              squeezingDate: editingPostFermentation?.squeezingDate ? new Date(editingPostFermentation.squeezingDate).toISOString().split('T')[0] : '',
              daborbageDate: editingPostFermentation?.daborbageDate ? new Date(editingPostFermentation.daborbageDate).toISOString().split('T')[0] : '',
              sg: editingPostFermentation?.sg || '',
              ph: editingPostFermentation?.ph || '',
              malic_acid: editingPostFermentation?.malic_acid || '',
              va: editingPostFermentation?.va || '',
              notes: editingPostFermentation?.notes || ''
            }}
            validationSchema={PostFermentationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="space-y-4">
                <div>
                  <label htmlFor="batch_id" className="form-label">
                    Wine Batch
                  </label>
                  <Field as="select" id="batch_id" name="batch_id" className="mobile-form-input" disabled={!!editingPostFermentation} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    // Convert the value to a number or null
                    const val = e.target.value ? parseInt(e.target.value, 10) : 0;
                    setFieldValue('batch_id', val);
                  }}>
                    <option value="">Select a Wine Batch</option>
                    {wineBatches.length > 0 ? (
                      wineBatches.map((batch) => {
                        // Handle different possible structures for batch data
                        const batchId = batch.batch_id || batch.batchId || `#${batch.id}`;
                        const batchName = batch.batch_name || batch.batchName || 'Unnamed Batch';
                        const vineyardName = batch.vineyard?.name || batch.Vineyard?.name || '';

                        console.log(`Rendering batch option: ID=${batch.id}, batchId=${batchId}, batchName=${batchName}, vineyard=${vineyardName}`);

                        return (
                          <option key={batch.id} value={batch.id}>
                            {vineyardName ? `${vineyardName} - ` : ''}{batchId} - {batchName}
                          </option>
                        );
                      })
                    ) : (
                      <option value="" disabled>No wine batches with fermentation available</option>
                    )}
                  </Field>
                  {wineBatches.length === 0 && !loading && (
                    <p className="mt-1 text-sm text-monday-text-muted">No wine batches with fermentation records found. Please ensure fermentation processes exist.</p>
                  )}
                  <ErrorMessage name="batch_id" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="squeezingDate" className="form-label">
                    Pressing Date <span className="text-monday-text-muted text-xs">(optional)</span>
                  </label>
                  <Field type="date" id="squeezingDate" name="squeezingDate" className="mobile-form-input" />
                  <ErrorMessage name="squeezingDate" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="daborbageDate" className="form-label">
                    Débourbage Date <span className="text-monday-text-muted text-xs">(optional)</span>
                  </label>
                  <Field type="date" id="daborbageDate" name="daborbageDate" className="mobile-form-input" />
                  <ErrorMessage name="daborbageDate" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="va" className="form-label">
                    Volatile Acidity (VA) <span className="text-monday-text-muted text-xs">(optional)</span>
                  </label>
                  <Field
                    type="number"
                    id="va"
                    name="va"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 0.4"
                    className="mobile-form-input"
                  />
                  <ErrorMessage name="va" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="sg" className="form-label">
                      SG <span className="text-monday-text-muted text-xs">(optional)</span>
                    </label>
                    <Field type="number" step="0.001" id="sg" name="sg" className="mobile-form-input" placeholder="e.g., 0.998" />
                    <ErrorMessage name="sg" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                  <div>
                    <label htmlFor="ph" className="form-label">
                      pH <span className="text-monday-text-muted text-xs">(optional)</span>
                    </label>
                    <Field type="number" step="0.01" id="ph" name="ph" className="mobile-form-input" placeholder="e.g., 3.45" />
                    <ErrorMessage name="ph" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                  <div>
                    <label htmlFor="malic_acid" className="form-label">
                      Malic Acid <span className="text-monday-text-muted text-xs">(optional)</span>
                    </label>
                    <Field type="number" step="0.01" id="malic_acid" name="malic_acid" className="mobile-form-input" placeholder="e.g., 1.80" />
                    <ErrorMessage name="malic_acid" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div>
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <Field as="textarea" id="notes" name="notes" rows={3} className="mobile-form-input resize-none" />
                  <ErrorMessage name="notes" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isSubmitting} className="mobile-btn mobile-btn-primary w-full sm:w-auto">
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default PostFermentation;
